import { Icon, token } from "@may-i/mash-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";

interface LineZoneCardProps {
  type: "line" | "zone";
  /** 캔버스 선택 동기화용 (`data-line-zone-id={type}-{id}` 으로 노출) */
  id?: number;
  name: string;
  keySuffix: string;
  keyPrefix: string;
  isDuplicateName?: boolean;
  isEmptyName?: boolean;
  isCrossCameraDuplicateName?: boolean;
  duplicateNameLabel?: string;
  emptyNameLabel?: string;
  crossCameraDuplicateNameLabel?: string;
  isDuplicateKey?: boolean;
  isEmptyKey?: boolean;
  /** Key suffix 가 영문/숫자/하이픈 포맷 규칙 위반 (PO QA 2차 m) */
  isInvalidKeyFormat?: boolean;
  duplicateKeyLabel?: string;
  emptyKeyLabel?: string;
  invalidKeyFormatLabel?: string;
  missingFloorPlanHint?: string;
  /** 스냅샷에 안 그려진 라인/구역 (도면에만 있음) */
  missingSnapshotHint?: string;
  selected?: boolean;
  onClick?: () => void;
  onNameChange?: (value: string) => void;
  onKeyChange?: (value: string) => void;
  onDelete?: () => void;
}

export function LineZoneCard({
  type,
  id,
  name,
  keySuffix,
  keyPrefix,
  isDuplicateName,
  isEmptyName,
  isCrossCameraDuplicateName,
  duplicateNameLabel,
  emptyNameLabel,
  crossCameraDuplicateNameLabel,
  isDuplicateKey,
  isEmptyKey,
  isInvalidKeyFormat,
  duplicateKeyLabel,
  emptyKeyLabel,
  invalidKeyFormatLabel,
  missingFloorPlanHint,
  missingSnapshotHint,
  selected,
  onClick,
  onNameChange,
  onKeyChange,
  onDelete,
}: LineZoneCardProps) {
  const { t } = useTranslation("analysis-draft");
  const { data: currentUser } = useCurrentUser();
  const isStaff = currentUser?.isStaff ?? false;
  const [keyFocused, setKeyFocused] = useState(false);
  const showKeyHelp = keyFocused && !isDuplicateKey && !isEmptyKey && !isInvalidKeyFormat;
  return (
    <Container
      $selected={selected}
      $type={type}
      data-line-zone
      data-line-zone-id={id != null ? `${type}-${id}` : undefined}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    >
      <ColorBar $type={type} />
      <Fields>
        <NameFieldColumn>
          <FieldRow>
            <FieldLabel>{t("label.name")}</FieldLabel>
            <NameInput
              $hasError={isDuplicateName || isEmptyName}
              value={name}
              onChange={(e) => onNameChange?.(e.target.value)}
              onFocus={() => onClick?.()}
              placeholder={t("label.namePlaceholder")}
            />
          </FieldRow>
          {isDuplicateName && (
            <ErrorText>{duplicateNameLabel}</ErrorText>
          )}
          {!isDuplicateName && isEmptyName && (
            <ErrorText>{emptyNameLabel}</ErrorText>
          )}
          {!isDuplicateName && !isEmptyName && isCrossCameraDuplicateName && (
            <WarningText>{crossCameraDuplicateNameLabel}</WarningText>
          )}
        </NameFieldColumn>
        {/* 2-2: 고객사는 event key 미노출 (이름만 오픈). 모델은 그대로 유지 (검증/저장에 영향 없음) */}
        {isStaff && (
          <KeyFieldColumn>
            <FieldRow>
              <FieldLabel>Key</FieldLabel>
              <KeyRow>
                <KeyPrefix>{keyPrefix}</KeyPrefix>
                <KeyInput
                  $hasError={isDuplicateKey || isEmptyKey || isInvalidKeyFormat}
                  value={keySuffix}
                  onChange={(e) => onKeyChange?.(e.target.value)}
                  onFocus={() => { onClick?.(); setKeyFocused(true); }}
                  onBlur={() => setKeyFocused(false)}
                  placeholder="key"
                />
              </KeyRow>
            </FieldRow>
            {isDuplicateKey && (
              <ErrorText>{duplicateKeyLabel}</ErrorText>
            )}
            {!isDuplicateKey && isEmptyKey && (
              <ErrorText>{emptyKeyLabel}</ErrorText>
            )}
            {!isDuplicateKey && !isEmptyKey && isInvalidKeyFormat && (
              <ErrorText>{invalidKeyFormatLabel}</ErrorText>
            )}
            {showKeyHelp && (
              <HelpText>{t("hint.keyFormat")}</HelpText>
            )}
          </KeyFieldColumn>
        )}
        {missingFloorPlanHint && (
          <HintText>{missingFloorPlanHint}</HintText>
        )}
        {missingSnapshotHint && (
          <HintText>{missingSnapshotHint}</HintText>
        )}
      </Fields>
      {onDelete && (
        <DeleteButton
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title={t("label.delete")}
        >
          <Icon icon="ic_remove" width={14} />
        </DeleteButton>
      )}
    </Container>
  );
}

// --- Styles ---

const Container = styled.div<{ $selected?: boolean; $type: "line" | "zone" }>`
  display: flex;
  gap: 6px;
  background: ${({ $selected, $type }) =>
    $selected
      ? $type === "line"
        ? "rgba(99, 251, 42, 0.16)"
        : "rgba(0, 224, 253, 0.16)"
      : "transparent"};
  border-radius: 4px;
  position: relative;
`;

const ColorBar = styled.div<{ $type: "line" | "zone" }>`
  width: 2px;
  flex-shrink: 0;
  align-self: stretch;
  background: ${({ $type }) => ($type === "line" ? "#63FB2A" : "#00E0FD")};
  border-radius: 1px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FieldLabel = styled.span`
  flex-shrink: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #696f8c;
`;

const FieldInput = styled.input`
  flex: 1;
  height: 24px;
  border: none;
  border-bottom: 1px solid rgba(143, 149, 178, 0.32);
  background: transparent;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  outline: none;
  padding: 0;
  min-width: 0;
`;

const NameInput = styled(FieldInput)<{ $hasError?: boolean }>`
  border-bottom-color: ${({ $hasError }) => ($hasError ? "#FA332D" : "rgba(143, 149, 178, 0.32)")};

  &:focus {
    border-bottom-color: ${({ $hasError }) => ($hasError ? "#FA332D" : token("colors.action.active"))};
  }
`;

const NameFieldColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const KeyInput = styled(FieldInput)<{ $hasError?: boolean }>`
  border-bottom-color: ${({ $hasError }) => ($hasError ? "#FA332D" : "rgba(143, 149, 178, 0.32)")};

  &:focus {
    border-bottom-color: ${({ $hasError }) => ($hasError ? "#FA332D" : token("colors.action.active"))};
  }
`;

const KeyFieldColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ErrorText = styled.span`
  padding: 2px 0 0 30px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.5;
  color: #FA332D;
`;

const WarningText = styled.span`
  padding: 2px 0 0 30px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.5;
  color: #EB6227;
`;

const HintText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: #1890FF;
`;

// 입력 중 안내 (key 포맷 등) — 회색 톤 (PO QA 2차 m 추가)
const HelpText = styled.span`
  padding: 2px 0 0 30px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;

const KeyRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex: 1;
  min-width: 0;
`;

const KeyPrefix = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: #696f8c;
  white-space: nowrap;
  flex-shrink: 0;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 6px;
  right: 0;
  padding: 0;
  border: none;
  background: none;
  color: ${token("colors.text.secondary")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;

  ${Container}:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`;
